import OccupancyItemVertical from 'components/Common/OccupancyPicker/OccupancyItemVertical'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import InfantSeatNoticeMessage from 'components/Flights/Common/InfantSeatNoticeMessage/InfantSeatNoticeMessage'
import {
  DEFAULT_ADULT_OCCUPANTS,
  maxFlightChildAge,
  maxFlightInfantAge,
  maxFlightPassengerCount,
} from 'constants/flight'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import TextButton from 'LuxKit/Button/TextButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

const TravellersContainer = styled(VerticalSpacer)`
  ${mediaQueryUp.tablet} {
    width: ${rem(416)};
  }
`

interface Props {
  occupants: App.Occupants
  onChange: (occupants: App.Occupants) => void
}

export const DEFAULT_TRANSFER_OCCUPANTS: App.Occupants = {
  adults: DEFAULT_ADULT_OCCUPANTS,
  children: 0,
  childrenAge: [],
  infants: 0,
}

function TransferTravellerSelect({ occupants, onChange }: Props) {
  const intl = useIntl()
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const numberOfOccupants = occupants.adults + (occupants.children || 0) + (occupants.infants || 0)

  const label = useMemo(
    () =>
      intl.formatMessage(
        {
          id: 'QiDFr5ISVUaDCA2leWye',
          defaultMessage: '{count, plural, one {# Traveller} other {# Travellers}}',
          description: 'Traveller count label in flight search widget in the flights section',
        },
        { count: numberOfOccupants },
      ),
    [intl, numberOfOccupants],
  )

  const applyText = useMemo(
    () =>
      intl.formatMessage({
        id: 'XV_uWUPjmcqL5r9jwE-x',
        defaultMessage: 'Apply',
        description: 'Button text to apply traveller selection in flight search',
      }),
    [intl],
  )

  const handleOpen = useCallback(() => {
    setOpen(current => !current)
  }, [])

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [])

  const onChangeOccupants = useCallback(
    (_itemId: number, nextOccupancy: App.Occupants) => {
      const infants = nextOccupancy.adults < (nextOccupancy.infants || 0)
        ? nextOccupancy.adults
        : nextOccupancy.infants
      onChange({ ...nextOccupancy, infants })
    },
    [onChange],
  )

  const capacity = useMemo(() => ([{
    adults: maxFlightPassengerCount,
    children: maxFlightPassengerCount,
    teenagers: 0,
    infants: Math.min(maxFlightPassengerCount, occupants.adults),
    total: maxFlightPassengerCount,
  }]), [occupants.adults])

  const childrenAgeLabel = intl.formatMessage({
    id: 'XxZPAoKaIAOJsGJc15lZ',
    defaultMessage: 'at time of return flight',
    description: 'Age label clarification for children in flight traveller selection',
  })

  const childrenLabel = intl.formatMessage(
    {
      id: 'u94b99AQC5n2W-p3Z32f',
      defaultMessage: 'Aged {minAge} - {maxAge}',
      description: 'Age range label for children in flight traveller selection',
    },
    { minAge: maxFlightInfantAge + 1, maxAge: maxFlightChildAge },
  )

  return (
    <div>
      <TextButton
        kind="tertiary"
        startIcon={<LineUserIcon />}
        endIcon={<DropdownChevron open={open} />}
        onClick={handleOpen}
        ref={triggerRef}
        data-testid="transfer-travellers"
      >
        {label}
      </TextButton>

      <DropdownSheet
        size="M"
        open={open}
        onClose={handleClose}
        anchorRef={triggerRef}
        triggerRef={triggerRef}
        primaryActionProps={{
          'data-testid': 'apply-transfer-traveller-changes',
          children: applyText,
          onClick: handleClose,
        }}
      >
        <TravellersContainer gap={24}>
          <OccupancyItemVertical
            itemId={0}
            occupancy={occupants}
            onChange={onChangeOccupants}
            capacities={capacity}
            childVariant="infants"
            maxChildAge={maxFlightChildAge}
            maxInfantAge={maxFlightInfantAge}
            childrenAgeLabel={childrenAgeLabel}
            childrenLabel={childrenLabel}
          />

          {!!occupants.infants && <InfantSeatNoticeMessage minified />}
        </TravellersContainer>
      </DropdownSheet>
    </div>
  )
}

export default TransferTravellerSelect
